import clsx from 'clsx'
import { useState } from 'react'

import { Shimmer } from '@wartek-id/shimmer'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'

import { assetUrl } from 'utils/url'
import useToggleSdkContext from 'utils/useToogleSdk'
import { useAPIGetStatusPengaliranAkhir } from 'api/kinerja/useAPIKinerja'
import { formatDate } from 'utils/datetime'
import { addPeriodSuffix } from 'utils/text'

import type { TPengaliranStatus } from 'types/kinerja-pelaksanaan'

type IPengaliranStatic = {
  [key in TPengaliranStatus]: {
    color: string
    icon: string
    description: string
    getTitle: (year: number) => string
  }
}

const STATUS_DETAIL: IPengaliranStatic = {
  SUCCESS: {
    color: 'border-green-50 bg-green-5',
    icon: 'check_circle_outline',
    getTitle: (year: number) =>
      `Predikat Kinerja Tahun ${year} berhasil dialirkan ke E-Kinerja`,
    description: 'Kunjungi E-Kinerja untuk cek data.',
  },
  FAILED: {
    color: 'border-red-60 bg-red-5',
    icon: 'warning',
    getTitle: (year: number) =>
      `Predikat Kinerja Tahun ${year} gagal dialirkan ke E-Kinerja`,
    description:
      'Mohon kunjungi E-Kinerja lalu tekan tombol “Penilaian”. Setelah itu, tekan tombol “Sinkro Penilaian” dan ikuti petunjuk yang tersedia.',
  },
  NOT_SENT: {
    color: 'border-blue-70 bg-blue-5',
    icon: 'hourglass_empty',
    description:
      'Mohon kunjungi E-Kinerja lalu tekan tombol “Penilaian”. Setelah itu, tekan tombol “Sinkro Penilaian” dan ikuti petunjuk yang tersedia.',
    getTitle: (year: number) =>
      `Predikat Kinerja Tahun ${year} belum dialirkan ke E-Kinerja`,
  },
}

export default function BannerStatusPengaliranAkhir({
  year,
  className,
}: {
  year: number
  className?: string
}) {
  const [isOpen, setIsOpen] = useState(false)

  const toggleValue = useToggleSdkContext()
  const isBannerStatusPengaliranEnabled =
    toggleValue?.['kinerja-banner-status-pengaliran-enabled'] === true

  const { data, isLoading } = useAPIGetStatusPengaliranAkhir({
    params: {
      skp_year: year,
    },
    options: {
      enabled: isBannerStatusPengaliranEnabled && !Number.isNaN(year),
      useErrorBoundary: false,
    },
  })

  const status = data?.data?.status || ''

  const statusDetail = STATUS_DETAIL[status]

  if (isLoading) return <Shimmer className={clsx('!h-36 !w-full', className)} />

  if (!isBannerStatusPengaliranEnabled || !data?.data?.asn || !statusDetail)
    return null

  return (
    <div
      className={clsx(
        'flex p-3 border rounded gap-2',
        statusDetail.color,
        className
      )}
    >
      <Icon>{statusDetail.icon}</Icon>
      <div>
        <Text variant="heading-sm" className="mb-1">
          {statusDetail.getTitle(year)}
        </Text>

        <div className="mb-4">
          {status === 'FAILED' && (
            <Text variant="body-sm" color="subdued">
              {`Pesan kendala dari E-Kinerja: ${addPeriodSuffix(
                data?.data?.message || '-'
              )}`}
            </Text>
          )}
          <Text variant="body-sm" color="subdued" className="mb-4">
            {statusDetail.description}
          </Text>
          {(status === 'SUCCESS' || status === 'FAILED') && (
            <Text variant="body-sm" color="subdued">
              {`Pengaliran terakhir dilakukan pada: ${formatDate(
                data?.data?.createdAt,
                'dd MMMM yyyy, p'
              )} WIB`}
            </Text>
          )}
        </div>

        <div className="flex gap-1">
          <Button
            as="a"
            size="sm"
            color="black"
            href="https://kinerja.bkn.go.id/skp"
            target="_blank"
          >
            Ke E-Kinerja
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(true)}>
            Cek panduan selengkapnya
          </Button>
        </div>
      </div>
      <Dialog
        customSize={604}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        autoFocus={false}
      >
        <DialogOverlay />
        <DialogContent>
          <DialogHeader>
            <Text variant="display-sm">
              Panduan Pengaliran Predikat Kinerja
            </Text>
          </DialogHeader>
          <div className="w-full flex flex-col gap-4">
            <ul className="space-y-4">
              <li className="flex space-x-3 items-center px-6 pt-6">
                <div className="w-8 h-8 rounded-full flex justify-center items-center flex-shrink-0 border-subdued border">
                  <Text variant="body-sm">1</Text>
                </div>
                <div className="space-y-1">
                  <Text variant="body">
                    Tekan tombol
                    <strong> “Ke E-Kinerja” </strong>
                    pada kotak pemberitahuan
                  </Text>
                </div>
              </li>
              <img
                className="px-6 pb-2"
                alt="tekan tombol E-Kinerja pada kotak pemberitahuan"
                src={assetUrl(
                  '/images/pengelolaan-kinerja/status-pengaliran/pengaliran-1.png'
                )}
              />
              <hr />
              <li className="flex space-x-3 items-center px-6">
                <div className="w-8 h-8 rounded-full flex justify-center items-center flex-shrink-0 border-subdued border">
                  <Text variant="body-sm">2</Text>
                </div>
                <div className="space-y-1">
                  <Text variant="body">
                    Di E-Kinerja, tekan tombol <strong>“Penilaian”</strong> di
                    SKP terkait
                  </Text>
                </div>
              </li>
              <img
                className="px-6 pb-2"
                alt="tekan tombol penilaian di SKP terkait"
                src={assetUrl(
                  '/images/pengelolaan-kinerja/status-pengaliran/pengaliran-2.png'
                )}
              />
              <hr />
              <li className="flex space-x-3 items-center px-6">
                <div className="w-8 h-8 rounded-full flex justify-center items-center flex-shrink-0 border-subdued border">
                  <Text variant="body-sm">3</Text>
                </div>
                <div className="space-y-1">
                  <Text variant="body">
                    Tekan tombol <strong>“Sinkro Penilaian”</strong> dan ikuti
                    petunjuk yang tersedia
                  </Text>
                </div>
              </li>
              <img
                className="px-6 pb-6"
                alt="tekan tombol Sinkro Penilaian dan ikuti petunjuk yang tersedia"
                src={assetUrl(
                  '/images/pengelolaan-kinerja/status-pengaliran/pengaliran-3.png'
                )}
              />
            </ul>
          </div>
          <DialogFooter>
            <Button color="black" onClick={() => setIsOpen(false)} size="lg">
              Saya mengerti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
